import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';

/**
 * ClientPreferencesContext — manages user-side display preferences:
 *  - fontSize: 'sm' | 'md' | 'lg' | 'xl'
 *  - theme:    'light' | 'dark' | 'auto'
 *
 * Both persist in localStorage and are applied to the <html> element so that
 * every page on the client side reflects the choice without needing extra
 * imports.
 *
 * Why one context for both? They share the same persistence scope (per
 * device/browser) and the same target element. Bundling reduces ceremony.
 */

const FONT_SIZE_KEY = 'demarre_font_size_v1';
const THEME_KEY = 'demarre_theme_v1';
const HIGH_CONTRAST_KEY = 'demarre_high_contrast_v1';

// Tailwind base is 16px (1rem). We scale the html font-size via inline style.
const FONT_SIZE_MAP = {
  sm: '14px',
  md: '16px',
  lg: '18px',
  xl: '20px',
};

const ClientPreferencesContext = createContext({
  fontSize: 'md',
  theme: 'auto',
  resolvedTheme: 'light',
  highContrast: false,
  setFontSize: () => {},
  setTheme: () => {},
  setHighContrast: () => {},
});

export const ClientPreferencesProvider = ({ children }) => {
  const [fontSize, setFontSizeState] = useState(() => {
    try { return localStorage.getItem(FONT_SIZE_KEY) || 'md'; } catch { return 'md'; }
  });
  const [theme, setThemeState] = useState(() => {
    try { return localStorage.getItem(THEME_KEY) || 'auto'; } catch { return 'auto'; }
  });
  // Iter 320av — High contrast mode. Independent from theme (works on top
  // of light or dark). Boosts colour saturation, adds underlines to links,
  // strengthens text weight and enlarges focus rings.
  const [highContrast, setHighContrastState] = useState(() => {
    try { return localStorage.getItem(HIGH_CONTRAST_KEY) === '1'; } catch { return false; }
  });
  const [systemDark, setSystemDark] = useState(() => {
    try { return window.matchMedia('(prefers-color-scheme: dark)').matches; } catch { return false; }
  });

  // Listen to OS-level color scheme changes to update 'auto' resolution.
  useEffect(() => {
    let mq;
    try {
      mq = window.matchMedia('(prefers-color-scheme: dark)');
    } catch { return; }
    const handler = (e) => setSystemDark(e.matches);
    if (mq.addEventListener) mq.addEventListener('change', handler);
    else if (mq.addListener) mq.addListener(handler);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener('change', handler);
      else if (mq.removeListener) mq.removeListener(handler);
    };
  }, []);

  const resolvedTheme = theme === 'auto' ? (systemDark ? 'dark' : 'light') : theme;

  // Apply font-size to <html>. Tailwind rem-based utilities scale automatically.
  useEffect(() => {
    const px = FONT_SIZE_MAP[fontSize] || FONT_SIZE_MAP.md;
    document.documentElement.style.fontSize = px;
  }, [fontSize]);

  // Apply dark class to <html>.
  useEffect(() => {
    const html = document.documentElement;
    if (resolvedTheme === 'dark') html.classList.add('dark');
    else html.classList.remove('dark');
  }, [resolvedTheme]);

  // Iter 320av — Apply high-contrast class to <html>. Independent from
  // theme so it stacks with light OR dark backgrounds.
  useEffect(() => {
    const html = document.documentElement;
    if (highContrast) html.classList.add('high-contrast');
    else html.classList.remove('high-contrast');
  }, [highContrast]);

  const setFontSize = useCallback((val) => {
    if (!FONT_SIZE_MAP[val]) return;
    setFontSizeState(val);
    try { localStorage.setItem(FONT_SIZE_KEY, val); } catch { /* ignore */ }
  }, []);

  const setTheme = useCallback((val) => {
    if (!['light', 'dark', 'auto'].includes(val)) return;
    setThemeState(val);
    try { localStorage.setItem(THEME_KEY, val); } catch { /* ignore */ }
  }, []);

  const setHighContrast = useCallback((val) => {
    const bool = !!val;
    setHighContrastState(bool);
    try { localStorage.setItem(HIGH_CONTRAST_KEY, bool ? '1' : '0'); } catch { /* ignore */ }
  }, []);

  return (
    <ClientPreferencesContext.Provider value={{ fontSize, theme, resolvedTheme, highContrast, setFontSize, setTheme, setHighContrast }}>
      {children}
    </ClientPreferencesContext.Provider>
  );
};

export const useClientPreferences = () => useContext(ClientPreferencesContext);
